import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider } from "@/context/AuthContext";
import { FinanceProvider } from "@/context/FinanceContext";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Assessment from "@/pages/Assessment";
import FreeResult from "@/pages/FreeResult";
import Paywall from "@/pages/Paywall";
import PaymentReturn from "@/pages/PaymentReturn";
import Dashboard from "@/pages/Dashboard";
import Budget from "@/pages/Budget";
import Resilience from "@/pages/Resilience";
import History from "@/pages/History";
import Profile from "@/pages/Profile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import Catatan from "@/pages/Catatan";
import QuickEntry from "@/pages/QuickEntry";
import FinanceSetup from "@/pages/FinanceSetup";
import Simulator from "@/pages/Simulator";
import Community from "@/pages/Community";
import { Link } from "react-router-dom";

// Tabs that require a completed resilience assessment. For tracking-first users
// (logged in, no assessment) render a friendly CTA instead of crashing.
function RequireReport({ children }) {
  const { report } = useApp();
  if (report) return children;
  return (
    <div className="px-6 pt-16 text-center" data-testid="need-assessment">
      <h2 className="font-heading text-xl font-extrabold text-zinc-900">Belum ada penilaian ketahanan</h2>
      <p className="mx-auto mt-2 max-w-xs text-sm text-zinc-500">
        Lakukan Cek Ketahanan Keluarga (±2 menit) untuk membuka Beranda, Ketahanan, dan Anggaran.
      </p>
      <Link
        to="/assessment"
        data-testid="need-assessment-cta"
        className="mt-6 inline-flex h-12 items-center rounded-full bg-zinc-900 px-6 text-sm font-semibold text-white"
      >
        Mulai Cek Ketahanan
      </Link>
      <p className="mt-4 text-xs text-zinc-400">Atau lanjutkan mencatat transaksi di tab Catatan.</p>
    </div>
  );
}

// Premium gate: only users with an ACTIVE subscription (payment or voucher)
// may access the daily-tracking product. Others are sent to the Paywall.
function RequirePremium({ children }) {
  const { premium, subscriptionLoading } = useApp();
  const { loading: authLoading, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();
  if (authLoading || (isAuthenticated && subscriptionLoading)) {
    return (
      <div className="px-6 pt-24 text-center text-sm text-zinc-400" data-testid="premium-gate-loading">
        Memuat…
      </div>
    );
  }
  if (premium || isAdmin) return children;
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/paywall?next=${next}`} replace />;
}

function App() {
  return (
    <div className="App min-h-screen bg-[#f8f8f7]">
      <AuthProvider>
        <AppProvider>
          <FinanceProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/assessment" element={<Assessment />} />
              <Route path="/result" element={<FreeResult />} />
              <Route path="/paywall" element={<Paywall />} />
              <Route path="/payment/return" element={<PaymentReturn />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/lupa-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/app" element={<AppLayout />}>
                <Route index element={<Navigate to="/app/beranda" replace />} />
                <Route path="beranda" element={<RequirePremium><Dashboard /></RequirePremium>} />
                <Route path="catatan" element={<RequirePremium><Catatan /></RequirePremium>} />
                <Route path="catat" element={<RequirePremium><QuickEntry /></RequirePremium>} />
                <Route path="keuangan" element={<RequirePremium><FinanceSetup /></RequirePremium>} />
                <Route path="simulasi" element={<RequirePremium><Simulator /></RequirePremium>} />
                <Route path="komunitas" element={<Community />} />
                <Route path="anggaran" element={<RequirePremium><RequireReport><Budget /></RequireReport></RequirePremium>} />
                <Route path="ketahanan" element={<Resilience />} />
                <Route path="riwayat" element={<History />} />
                <Route path="profil" element={<Profile />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" />
          </FinanceProvider>
        </AppProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
